import normalizeLabel from './normalizeLabel';

/**
 * Matches the imported effect bands / question columns against the meetstandaard catalog
 * BY TEXT. The effect band scopes the question lookup, so identical question wording
 * across different effects stays unambiguous.
 *
 * @typedef {object} CatalogQuestion
 * @property {string} id - The meetstandaard question id (becomes MSIId on the row).
 * @property {string} name - The question text (becomes the MatrixQuestionRow Title).
 * @property {'negative'|'positive'} [posNeg] - Whether the scale is inverted.
 *
 * @typedef {object} CatalogEffect
 * @property {string} id - The meetstandaard effect id (becomes MSIId on effect/field).
 * @property {string} name - The effect name.
 * @property {string} [description]
 * @property {string[]} [sectors]
 * @property {string} category - The parent category name (becomes MainEffect).
 * @property {CatalogQuestion[]} questions
 *
 * @typedef {object} ColumnSuggestion
 * @property {number} colIndex - Data-row column index this suggestion covers.
 * @property {string} effectName - Raw effect-band name from the file.
 * @property {string} questionName - Raw question header from the file.
 * @property {CatalogEffect|null} effect - Matched catalog effect, or null.
 * @property {CatalogQuestion|null} question - Matched catalog question, or null.
 */

/**
 * Flattens the API catalog (categories → effects → questions) into a flat effect list,
 * each effect carrying its parent category name. Used by the matcher and the review UI.
 *
 * @param {Array<{name?: string, effects?: CatalogEffect[]}>} catalog - Meetstandaard API payload.
 * @returns {CatalogEffect[]}
 */
const flattenMeetstandaardCatalog = (catalog) => {
    if (!Array.isArray(catalog)) return [];
    return catalog.flatMap((category) =>
        (category?.effects || []).map((effect) => ({
            id: effect.id,
            name: effect.name,
            description: effect.description || '',
            sectors: Array.isArray(effect.sectors) ? effect.sectors : [],
            category: category?.name || '',
            // Provenance of a versioned effect. Absent for the legacy /database
            // catalogue, which publishes no version — buildImportPlan then
            // derives one from `sectors` instead.
            // AI-CONTEXT: See docs/features/msi-version-stamping.md
            standaard: effect.standaard,
            standaardVersion: effect.standaardVersion,
            questions: Array.isArray(effect.questions) ? effect.questions : [],
        }))
    );
};

/**
 * @param {import('./parseDatasetWorkbook').ParsedEffectBand[]} effectBands - From parseDatasetWorkbook.
 * @param {Array<{name?: string, effects?: CatalogEffect[]}>} catalog - Meetstandaard API payload.
 * @returns {ColumnSuggestion[]} One suggestion per question column, in column order.
 */
const matchColumnsToMeetstandaard = (effectBands, catalog) => {
    const flatEffects = flattenMeetstandaardCatalog(catalog);

    // The meetstandaard carries the same effect name as separate SECTOR VARIANTS
    // (e.g. "Fysieke gezondheid" for gelijke-kansen AND arbeidsparticipatie, with
    // different questions). Keep every variant; per band the variant whose
    // questions overlap the file's column headers wins — picking blindly the
    // first one left the other variant's columns unmatched and pushed users into
    // a manual pick that created a duplicate effect (inbox #77/#81).
    // AI-CONTEXT: See docs/pitfalls.md#api-meetstandaard-same-name-sector-variants
    /** @type {Map<string, CatalogEffect[]>} */
    const effectsByName = new Map();
    flatEffects.forEach((effect) => {
        const key = normalizeLabel(effect.name);
        if (!effectsByName.has(key)) effectsByName.set(key, []);
        effectsByName.get(key).push(effect);
    });

    /** Count how many of the band's question headers exist in this variant. */
    const questionOverlap = (effect, band) => {
        const names = new Set(effect.questions.map((q) => normalizeLabel(q.name)));
        return band.questions.filter((col) => names.has(normalizeLabel(col.questionName))).length;
    };

    /** @type {ColumnSuggestion[]} */
    const suggestions = [];

    (effectBands || []).forEach((band) => {
        const candidates = effectsByName.get(normalizeLabel(band.effectName)) || [];
        // Best question-overlap wins; stable tie-break on catalog order.
        const matchedEffect = candidates.reduce((best, candidate) => {
            if (!best) return candidate;
            return questionOverlap(candidate, band) > questionOverlap(best, band) ? candidate : best;
        }, null);

        /** @type {Map<string, CatalogQuestion>} */
        const questionByName = new Map();
        if (matchedEffect) {
            matchedEffect.questions.forEach((q) => {
                const key = normalizeLabel(q.name);
                if (!questionByName.has(key)) questionByName.set(key, q);
            });
        }

        band.questions.forEach((col) => {
            const matchedQuestion = matchedEffect
                ? questionByName.get(normalizeLabel(col.questionName)) || null
                : null;

            suggestions.push({
                colIndex: col.colIndex,
                effectName: band.effectName,
                questionName: col.questionName,
                effect: matchedEffect
                    ? {
                          id: matchedEffect.id,
                          name: matchedEffect.name,
                          description: matchedEffect.description,
                          sectors: matchedEffect.sectors,
                          category: matchedEffect.category,
                          // Dropping these here would silently downgrade a
                          // versioned effect to "legacy" in buildImportPlan,
                          // and the answers would lose their provenance.
                          standaard: matchedEffect.standaard,
                          standaardVersion: matchedEffect.standaardVersion,
                      }
                    : null,
                question: matchedQuestion
                    // posNeg must stay the canonical 'negative'/'positive' strings —
                    // FieldType and calculateCompanyGrowth compare === 'negative', so a
                    // boolean here silently disables the color flip and score inversion.
                    // AI-CONTEXT: See docs/pitfalls.md#database-string-enum-boolean-coercion
                    ? {
                          id: matchedQuestion.id,
                          name: matchedQuestion.name,
                          posNeg: matchedQuestion.posNeg === 'negative' || matchedQuestion.posNeg === true
                              ? 'negative'
                              : 'positive',
                          // Half of the versioned coordinate — the statement's
                          // position inside its pinned document.
                          stellingNummer: matchedQuestion.stellingNummer,
                      }
                    : null,
            });
        });
    });

    return suggestions;
};

export { flattenMeetstandaardCatalog };
export default matchColumnsToMeetstandaard;
